import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post } from "../services/apiClient";
import { businessKeys } from "./useBusinesses";

export interface OwnerBusinessSummary {
  id: string;
  businessName: string;
  nameEn: string;
  city: string;
  phone: string;
  email?: string | null;
  status: string;
  isVisible: boolean;
  avgRating?: number | null;
  reviewCount?: number | null;
  createdAt: string;
}

export interface OwnerSubscriptionSummary {
  id: string;
  status: string;
  isCustom?: boolean | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  startedAt?: string | null;
  billingCycle?: "MONTHLY" | "YEARLY" | "CUSTOM" | string | null;
  currency?: string | null;
  unitAmount?: number | null;
  gateway?: string | null;
  trialStartAt?: string | null;
  trialEndAt?: string | null;
  gracePeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  cancelledAt?: string | null;
  cancelReason?: string | null;
  plan?: {
    id: string;
    name: string;
    priceMonthly: number;
    maxBusinesses: number;
  } | null;
}

export interface OwnerEntity {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  whatsapp?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string;
  _count?: { businesses: number };
  businesses?: OwnerBusinessSummary[];
  subscriptions?: OwnerSubscriptionSummary[];
  visibleBusinessesCount?: number;
}

export interface OwnerListParams {
  search?: string;
  page?: number;
  limit?: number;
}

export interface OwnerListResponse {
  data: OwnerEntity[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateOwnerInput {
  name: string;
  phone: string;
  email?: string;
  whatsapp?: string;
  notes?: string;
}

export const ownerKeys = {
  all: ["owners"] as const,
  list: (params: OwnerListParams) =>
    [...ownerKeys.all, "list", params] as const,
  detail: (id: string) => [...ownerKeys.all, "detail", id] as const,
  subscription: (id: string) => [...ownerKeys.all, "subscription", id] as const,
};

export const useOwnerList = (params: OwnerListParams = {}) =>
  useQuery<OwnerListResponse>({
    queryKey: ownerKeys.list(params),
    queryFn: () => get<OwnerListResponse>("/owners", params),
  });

export const useOwner = (id: string | undefined) =>
  useQuery<OwnerEntity>({
    queryKey: ownerKeys.detail(id ?? ""),
    queryFn: () => get<OwnerEntity>(`/owners/${id}`),
    enabled: !!id,
  });

export const useCreateOwner = () => {
  const qc = useQueryClient();
  return useMutation<OwnerEntity, Error, CreateOwnerInput>({
    mutationFn: (data) => post<OwnerEntity>("/owners", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ownerKeys.all }),
  });
};

export const useToggleAllOwnerBusinesses = () => {
  const qc = useQueryClient();
  return useMutation<
    { success: boolean; affectedBusinessCount: number },
    Error,
    { id: string; isVisible: boolean }
  >({
    mutationFn: ({ id, isVisible }) =>
      post(`/owners/${id}/businesses/toggle-all`, { isVisible }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ownerKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
};

export const useSuspendAllOwnerBusinesses = () => {
  const qc = useQueryClient();
  return useMutation<
    { success: boolean; affectedBusinessCount: number },
    Error,
    { id: string }
  >({
    mutationFn: ({ id }) => post(`/owners/${id}/businesses/suspend-all`, {}),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ownerKeys.detail(v.id) });
      qc.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
};

export const useOwnerSubscription = (id: string | undefined) =>
  useQuery<OwnerSubscriptionSummary | null>({
    queryKey: ownerKeys.subscription(id ?? ""),
    queryFn: () =>
      get<OwnerSubscriptionSummary | null>(`/owners/${id}/subscription`),
    enabled: !!id,
  });

export const useDeleteOwner = () => {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id: string) => del<{ success: boolean }>(`/owners/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ownerKeys.all });
      qc.invalidateQueries({ queryKey: businessKeys.all });
    },
  });
};
